import type { Laterality } from "@akina/domain";
import type { SharedFormatMessage } from "@akina/infra";

/**
 * Formats exercise name with laterality indication (L) or (R)
 * @param exerciseName - The base exercise name
 * @param laterality - The laterality (left, right, or both)
 * @param formatMessage - i18n format message function
 * @returns Formatted exercise name with laterality suffix or undefined
 */
export function formatExerciseNameWithLaterality(
  exerciseName: string | undefined,
  laterality: Laterality | null | undefined,
  formatMessage: SharedFormatMessage,
): string | undefined {
  if (!exerciseName) {
    return undefined;
  }

  if (!laterality || laterality === "both") {
    return exerciseName;
  }

  const leftAbbreviation = formatMessage({
    id: "shared.feedback.lateralityAbbreviation.left",
  }).trim();

  const rightAbbreviation = formatMessage({
    id: "shared.feedback.lateralityAbbreviation.right",
  }).trim();

  const abbreviation = laterality === "left" ? leftAbbreviation : rightAbbreviation;
  return abbreviation ? `${exerciseName} (${abbreviation})` : exerciseName;
}

/**
 * Extracts laterality from an array of exercise log summaries
 * Based on the pattern used in useMistakesLogic hook
 * @param exercises - Array of exercise log summaries
 * @returns The laterality or null if cannot be determined
 */
export function getExerciseLaterality<T extends { laterality: Laterality }>(
  exercises: T[] | null | undefined,
): Laterality | null {
  if (!exercises || exercises.length === 0) return null;

  // Single exercise: use its laterality directly
  if (exercises.length === 1) {
    return exercises[0].laterality;
  }

  // Multiple exercises: check what we have
  const hasLeft = exercises.some(ex => ex.laterality === "left");
  const hasRight = exercises.some(ex => ex.laterality === "right");
  const hasBoth = exercises.some(ex => ex.laterality === "both");

  // If only one laterality present, return it
  if (hasLeft && !hasRight && !hasBoth) return "left";
  if (hasRight && !hasLeft && !hasBoth) return "right";
  if (hasBoth && !hasLeft && !hasRight) return "both";

  // Multiple lateralities (left + right): return "both" to avoid showing (L) or (R)
  // This happens when the routine was performed on both sides
  return "both";
}

/**
 * Fallback helper to extract laterality from exercise template key
 * Use this when laterality field is not available from backend
 * @param exerciseKey - Exercise template key
 * @returns The laterality based on key naming convention
 */
export function getLateralityFromKey(exerciseKey: string): Laterality {
  const lowerKey = exerciseKey.toLowerCase();
  if (lowerKey.includes("left")) return "left";
  if (lowerKey.includes("right")) return "right";
  return "both";
}
